import { PlusIcon } from '@heroicons/react/20/solid'
import { ArrowDownTrayIcon, LinkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { useState } from 'react'

import { useGetInstallerLinkQuery, useGetTeamsQuery } from '../../app/apiSlice'
import { LINK_USER_DOCUMENTATION } from '../../app/constants'
import { teamBgColorMap } from '../../app/theme'
import { AdminDataTableSection, AdminPage, AdminPageHeader } from '../../common/components'
import { Button, Link, Table, Text } from '../../common/designs'
import { useDashboard } from '../../common/hooks'
import type { Team as ApiTeam } from '../../common/types/common'
import type { Team } from '../../common/utils/UtilityWrapper/types'
import { downloadFile } from '../../common/utils/utils'
import AddTeamModal from './AddTeamModal'
import EditTeamModal from './EditTeamModal'
import InstallationInstructionsModal from './InstallationInstructionsModal'

const InstallationCenter = () => {
  const { teams: dashboardTeams } = useDashboard()
  const [installTeam, setInstallTeam] = useState<Team | null>(null)
  const [editTeam, setEditTeam] = useState<ApiTeam | null>(null)
  const [isEditTeamModalOpen, setIsEditTeamModalOpen] = useState(false)
  const [isAddTeamModalOpen, setIsAddTeamModalOpen] = useState(false)

  const { data: apiTeams, isLoading } = useGetTeamsQuery()
  const { data: installerLink } = useGetInstallerLinkQuery()

  return (
    <AdminPage width='full'>
      <AdminPageHeader
        title='Agent Deployment'
        description='Set up teams and deploy desktop agents with team-specific installation instructions. The MSI installer is the same for all installations; team-specific invitation tokens are below.'
      />
      <AdminDataTableSection>
        <Table
          label='Teams & Deployment'
          description='Define data collection teams, manage the teams, and install agent on each team.'
          head={['Team name', 'Color', 'Cost', 'Currency', 'Installation', 'Action']}
          action={
            <div className='flex flex-wrap gap-3'>
              <Button
                disabled={isLoading}
                onClick={() => setIsAddTeamModalOpen(true)}
                iconStart={<PlusIcon />}
              >
                Add a new team
              </Button>
              <Button
                iconStart={<ArrowDownTrayIcon />}
                onClick={() => {
                  downloadFile(installerLink ?? '', 'workfellow-plug-in.msi')
                }}
                disabled={!installerLink}
              >
                Download Agent Installer
              </Button>
              <Button
                variant='secondary'
                onClick={() => window.open(LINK_USER_DOCUMENTATION, '_blank')}
                iconStart={<LinkIcon />}
              >
                Documentation
              </Button>
            </div>
          }
          body={
            apiTeams?.map((t) => {
              const dashTeam = dashboardTeams[t.team_id]
              return [
                t.team_name,
                <div
                  className={clsx('size-5 rounded-md', teamBgColorMap[t.color_index])}
                  key={`color-${t.id}`}
                />,
                t.cost_per_hour,
                t.currency,
                dashTeam ? (
                  <Link onClick={() => setInstallTeam(dashTeam)} key={`install-${t.id}`}>
                    Open install instructions
                  </Link>
                ) : (
                  <Text color='lighterGray' key={`install-${t.id}`}>
                    —
                  </Text>
                ),
                <div className='flex justify-end' key={`actions-${t.id}`}>
                  <Link
                    type='ghost'
                    onClick={() => {
                      setEditTeam(t)
                      setIsEditTeamModalOpen(true)
                    }}
                  >
                    Edit
                  </Link>
                </div>,
              ]
            }) ?? []
          }
        />
      </AdminDataTableSection>

      <AddTeamModal open={isAddTeamModalOpen} onClose={() => setIsAddTeamModalOpen(false)} />

      <EditTeamModal
        activeTeam={editTeam}
        open={isEditTeamModalOpen}
        onClose={() => setIsEditTeamModalOpen(false)}
      />

      <InstallationInstructionsModal
        activeTeam={installTeam}
        open={Boolean(installTeam)}
        onClose={() => setInstallTeam(null)}
      />
    </AdminPage>
  )
}

export default InstallationCenter

